import React, { useEffect, useState } from "react";
import { Save, Shuffle, Sparkles, ChefHat } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { resolveImageUrl } from "@/lib/site";

function formatError(detail) {
  if (!detail) return "Något gick fel";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function DailySpecialEditor() {
  const [override, setOverride] = useState({ item_id: null, note: "", is_manual: false });
  const [menuItems, setMenuItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [o, m, p] = await Promise.all([
        api.get("/admin/daily-special"),
        api.get("/menu"),
        api.get("/daily-special"),
      ]);
      setOverride({
        item_id: o.data.item_id || null,
        note: o.data.note || "",
        is_manual: !!o.data.is_manual,
      });
      setMenuItems(m.data || []);
      setPreview(p.data);
    } catch (err) {
      toast.error("Kunde inte ladda dagens förslag");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/daily-special", override);
      toast.success("Sparat");
      const { data } = await api.get("/daily-special");
      setPreview(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = menuItems.filter((it) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return it.name.toLowerCase().includes(s) || (it.category || "").toLowerCase().includes(s);
  });

  const selected = menuItems.find((it) => it.id === override.item_id);

  if (loading) return <div className="py-12 text-center text-[var(--text-muted)]">Laddar…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-black flex items-center gap-3">
          <Sparkles size={30} /> Dagens förslag
        </h1>
        <p className="text-[var(--text-muted)] mt-1">
          Sätt en manuell rekommendation som visas på hemsidan. Annars slumpas en rätt automatiskt från menyn varje dag.
        </p>
      </div>

      {/* Live preview */}
      {preview?.item && (
        <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
            <h3 className="font-serif-display text-xl font-bold">Visas just nu på hemsidan</h3>
            <span className={`text-xs font-semibold uppercase tracking-wider rounded-full px-3 py-1 ${
              preview.is_manual ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"
            }`}>
              {preview.is_manual ? "Handplockad" : "Slumpas idag"}
            </span>
          </div>
          <div className="p-6 flex gap-4">
            <div className="w-24 h-24 shrink-0 rounded-xl overflow-hidden bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center">
              {preview.item.image_url ? (
                <img src={resolveImageUrl(preview.item.image_url)} alt="" className="w-full h-full object-cover" />
              ) : (
                <ChefHat size={28} className="text-[var(--text-muted)]" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase text-[var(--text-muted)] tracking-wider">{preview.item.category}</div>
              <div className="font-serif-display text-2xl font-black">{preview.item.name}</div>
              {preview.item.description && (
                <div className="text-sm text-[var(--text-muted)] mt-1 line-clamp-2">{preview.item.description}</div>
              )}
              {preview.note && (
                <div className="mt-2 italic text-sm border-l-4 border-[var(--primary)] pl-3">{preview.note}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual override card */}
      <div className="bg-white border border-[var(--border)] rounded-2xl p-6 space-y-5">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="daily-special-manual-toggle"
            checked={override.is_manual}
            onChange={(e) => setOverride({ ...override, is_manual: e.target.checked })}
            className="w-5 h-5 accent-[var(--primary)]"
          />
          <span className="font-semibold">Använd manuell rekommendation</span>
          <span className="text-sm text-[var(--text-muted)]">
            (om avstängd slumpas en rätt automatiskt varje dag)
          </span>
        </label>

        {override.is_manual && (
          <>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Välj maträtt</label>
              <input
                type="text"
                data-testid="daily-special-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Sök på namn eller kategori..."
                className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
              />
              <div className="mt-2 max-h-64 overflow-y-auto border border-[var(--border)] rounded-xl divide-y divide-[var(--border)]">
                {filteredItems.slice(0, 30).map((it) => (
                  <button
                    key={it.id}
                    onClick={() => setOverride({ ...override, item_id: it.id })}
                    data-testid={`daily-special-item-${it.id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-[var(--surface-alt)] flex items-center gap-3 ${
                      override.item_id === it.id ? "bg-[var(--primary)]/10" : ""
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[var(--surface-alt)] overflow-hidden shrink-0 grid place-items-center">
                      {it.image_url ? <img src={resolveImageUrl(it.image_url)} alt="" className="w-full h-full object-cover" /> : <ChefHat size={14} className="text-[var(--text-muted)]" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{it.name}</div>
                      <div className="text-xs text-[var(--text-muted)]">{it.category} · {it.price} kr</div>
                    </div>
                    {override.item_id === it.id && (
                      <span className="text-[var(--primary)] font-bold">✓</span>
                    )}
                  </button>
                ))}
                {filteredItems.length === 0 && (
                  <div className="px-4 py-6 text-center text-sm text-[var(--text-muted)]">Inga matchande rätter.</div>
                )}
              </div>
              {selected && (
                <div className="mt-2 text-sm text-[var(--text-muted)]">Valt: <strong>{selected.name}</strong></div>
              )}
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">
                Extra text (valfri, visas som citat)
              </label>
              <textarea
                data-testid="daily-special-note"
                value={override.note}
                onChange={(e) => setOverride({ ...override, note: e.target.value })}
                rows={2}
                placeholder="ex. Med hemgjord aioli — perfekt till en kall öl."
                className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
              />
            </div>
          </>
        )}

        {!override.is_manual && (
          <div className="rounded-xl bg-[var(--surface-alt)] p-5 flex items-start gap-3">
            <Shuffle size={20} className="text-[var(--primary)] mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Auto-läge aktivt</div>
              <p className="text-sm text-[var(--text-muted)] mt-1">
                En rätt slumpas från hela menyn varje dag. Samma rätt visas åt alla besökare under dagen och byts sedan automatiskt.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="daily-special-save"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-6 py-3 font-semibold disabled:opacity-60"
          >
            <Save size={16} /> {saving ? "Sparar…" : "Spara"}
          </button>
        </div>
      </div>
    </div>
  );
}
